import React, { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useServeProducingRegionsScorecards } from '../../../utils/api-hooks/analytics-serve';
import { TileFrame, ScorecardRow } from './KpiScorecards';

const toNum = (v) => {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Headline from `primary_metric` (value + unit, e.g. "12 countries", "1 active", "1,434 shipments"). */
function formatMetricHeadline(metric) {
  if (metric?.value == null && metric?.value !== 0) return '—';
  const v = metric.value;
  const unit = metric.unit != null ? String(metric.unit).trim() : '';
  if (unit === 'countries') return `${v} countries`;
  if (unit === 'active') return `${v} active`;
  if (unit === 'shipments') {
    const n = toNum(v);
    return n != null ? `${n.toLocaleString()} shipments` : `${v} shipments`;
  }
  if (unit) return `${v} ${unit}`;
  return String(v);
}

function formatLbsApprox(n) {
  const num = toNum(n);
  if (num == null) return null;
  const abs = Math.abs(num);
  if (abs >= 1_000_000) return `≈ ${(num / 1_000_000).toFixed(1)}M lbs`;
  if (abs >= 10_000) return `≈ ${(num / 1_000).toFixed(0)}K lbs`;
  return `≈ ${num.toLocaleString()} lbs`;
}

/** `cards` from API uses `kpi.producing_regions.*`; keep fallbacks for older shapes. */
function pickTilesPayload(data) {
  if (!data || typeof data !== 'object') {
    return { producing: {}, weather: {}, arriving: {} };
  }
  const c = data.cards;
  if (c && typeof c === 'object') {
    return {
      producing:
        c['kpi.producing_regions.countries'] ||
        c['producing_regions.producing'] ||
        c['overview.producing'] ||
        c.producing ||
        {},
      weather:
        c['kpi.producing_regions.weather_alerts'] ||
        c['producing_regions.weather_alerts'] ||
        c.weather_alerts ||
        c.weather ||
        {},
      arriving:
        c['kpi.producing_regions.arriving_10d'] ||
        c['producing_regions.arriving_next_10d'] ||
        c.arriving_next_10d ||
        c.arriving ||
        {},
    };
  }
  return {
    producing: data.producing || data.tile1 || data.producing_tile || {},
    weather: data.weather_alerts || data.weather || data.tile2 || {},
    arriving: data.arriving_next_10d || data.arriving || data.tile3 || {},
  };
}

const ITEM_STATUS_CLASS = {
  producing: 'text-emerald-600 dark:text-emerald-400',
  ending_soon: 'text-amber-600 dark:text-amber-400',
  starting_soon: 'text-blue-600 dark:text-blue-400',
};

const ProducingTile = ({ producing, label, onClick, clickAriaLabel }) => {
  const pm = producing?.primary_metric;
  const headline =
    pm?.value != null || pm?.value === 0 ? formatMetricHeadline(pm) : '—';
  const items = Array.isArray(producing?.items) ? producing.items : [];
  const visible = toNum(producing?.meta?.visible_count);
  const pillCount = visible != null && visible > 0 ? visible : 3;
  const pills = items.slice(0, pillCount);
  const hidden = toNum(producing?.meta?.hidden_count);

  return (
    <TileFrame label={label} onClick={onClick} clickAriaLabel={clickAriaLabel}>
      <div className="mt-[4px] text-[20px] font-semibold leading-tight text-[#282A29] dark:text-white">{headline}</div>
      {pills.length > 0 ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {pills.map((x, i) => {
            const name = x?.name;
            if (!name) return null;
            const st = x?.status || x?.harvest_status;
            const color =
              ITEM_STATUS_CLASS[st] || 'text-[#282A29] dark:text-white';
            return (
              <span
                key={`${name}-${i}`}
                className={`text-[11px] font-medium ${color}`}
              >
                {name}
              </span>
            );
          })}
          {hidden != null && hidden > 0 ? (
            <span className="text-[11px] font-medium text-[#828382] dark:text-gray-400">
              +{hidden} more
            </span>
          ) : null}
        </div>
      ) : null}
    </TileFrame>
  );
};

const WeatherTile = ({ weather, label, onClick, clickAriaLabel }) => {
  const pm = weather?.primary_metric;
  const headline =
    pm?.value != null || pm?.value === 0
      ? formatMetricHeadline(pm)
      : '0 active';

  const items = Array.isArray(weather?.items) ? weather.items : [];
  const first = items[0] || {};
  const loc = [first?.region, first?.country].filter(Boolean).join(', ');
  const alertType = first?.alert_type;
  const severity = first?.severity;
  const sub = (() => {
    if (alertType && severity) return `${alertType} (${severity})`;
    if (alertType) return alertType;
    if (severity) return String(severity);
    return '';
  })();
  const more = items.length > 1 ? `+${items.length - 1} more` : '';

  return (
    <TileFrame label={label} onClick={onClick} clickAriaLabel={clickAriaLabel}>
      <div className="mt-[4px] text-[20px] font-semibold leading-tight text-[#282A29] dark:text-white">
        {headline}
      </div>
      {loc ? (
        <div className="mt-[6px] text-[11px] text-[#828382] dark:text-gray-400">{loc}</div>
      ) : null}
      {sub || more ? (
        <div className="mt-[2px] text-[11px] text-[#828382] dark:text-gray-400">
          {[sub, more].filter(Boolean).join(sub && more ? ' · ' : '')}
        </div>
      ) : null}
    </TileFrame>
  );
};

const ArrivingTile = ({ arriving, label, onClick, clickAriaLabel }) => {
  const pm = arriving?.primary_metric;
  const headline =
    pm?.value != null || pm?.value === 0 ? formatMetricHeadline(pm) : '—';
  const count = toNum(pm?.value);
  const isEmpty = count === 0;

  const totalBoxes = toNum(arriving?.meta?.total_boxes);
  const kgPerBox = toNum(arriving?.meta?.kg_per_box);
  const lbsEquiv = arriving?.meta?.lbs_equiv;

  const line1 =
    !isEmpty && totalBoxes != null
      ? `${Math.round(totalBoxes).toLocaleString()} boxes${kgPerBox != null ? ` (${kgPerBox} kg)` : ''}`
      : '';
  const line2 = !isEmpty && lbsEquiv != null ? formatLbsApprox(lbsEquiv) : '';

  return (
    <TileFrame label={label} onClick={onClick} clickAriaLabel={clickAriaLabel}>
      <div className="mt-[4px] text-[20px] font-semibold leading-tight text-[#282A29] dark:text-white">
        {headline}
      </div>
      {line1 ? (
        <div className="mt-[6px] text-[11px] text-[#828382] dark:text-gray-400">{line1}</div>
      ) : null}
      {line2 ? (
        <div className="mt-[2px] text-[11px] text-[#828382] dark:text-gray-400">{line2}</div>
      ) : null}
    </TileFrame>
  );
};

/**
 * View 2 — producing regions chart-bound stat strip (3 tiles).
 * Same shell as FOB Price Pulse scorecards (`TileFrame` + `ScorecardRow`).
 */
const AnalyticsTabWeatherMapScorecards = ({ apiParams }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const commodityKey = apiParams?.commodity || '';

  const producingQ = useServeProducingRegionsScorecards(commodityKey, {
    email: apiParams?.email,
    dateStart: apiParams?.dateStart,
    dateEnd: apiParams?.dateEnd,
  });

  const { producing, weather, arriving } = useMemo(
    () => pickTilesPayload(producingQ.data),
    [producingQ.data],
  );

  // Deep-link helpers — each scorecard navigates to /map with a preset in router state
  // (never the URL) so the map opens already focused on the relevant view.
  // Sidebar is collapsed on arrival so the map fills more of the viewport.
  const goToProducingRegions = useCallback(() => {
    navigate('/map', {
      state: {
        mapPreset: {
          commodity: commodityKey,
          layer: 'all',
          status: ['producing', 'ending_soon'],
          sidebarCollapsed: true,
        },
      },
    });
  }, [commodityKey, navigate]);

  const goToWeatherAlerts = useCallback(() => {
    navigate('/map', {
      state: {
        mapPreset: {
          commodity: commodityKey,
          layer: 'weather',
          panel: 'alerts',
          sidebarCollapsed: true,
        },
      },
    });
  }, [commodityKey, navigate]);

  const goToShipments = useCallback(() => {
    navigate('/map', {
      state: {
        mapPreset: {
          commodity: commodityKey,
          layer: 'shipments',
          panel: 'arrivals10',
          sidebarCollapsed: true,
        },
      },
    });
  }, [commodityKey, navigate]);

  if (!commodityKey) return null;

  return (
    <ScorecardRow>
      <ProducingTile
        producing={producing}
        label={t('analyticsTab.map.producing', 'Producing')}
        onClick={goToProducingRegions}
        clickAriaLabel={t(
          'analyticsTab.map.producingLink',
          'Open producing regions on the Global Produce Map',
        )}
      />
      <WeatherTile
        weather={weather}
        label={t('analyticsTab.map.weatherAlerts', 'Weather alerts')}
        onClick={goToWeatherAlerts}
        clickAriaLabel={t(
          'analyticsTab.map.weatherAlertsLink',
          'Open weather alerts on the Global Produce Map',
        )}
      />
      <ArrivingTile
        arriving={arriving}
        label={t('analyticsTab.map.arrivingNext', 'Arriving in 10 days')}
        onClick={goToShipments}
        clickAriaLabel={t(
          'analyticsTab.map.arrivingNextLink',
          'Open shipments arriving in 10 days on the Global Produce Map',
        )}
      />
    </ScorecardRow>
  );
};

export default AnalyticsTabWeatherMapScorecards;
